import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal/Reveal';
import Compliance from '../components/Compliance/Compliance';
import Timeline from '../components/Timeline/Timeline';
import heroImage from '../assets/images/biogas-plant.png';
import {
  IconShieldCheck,
  IconGlobeLeaf,
  IconCheckCircle,
  IconCertificate,
  IconArrowRight,
  IconPhone,
  IconMail,
  IconLocation,
} from '../components/Icons/Icons';
import '../assets/css/compliance-page.css';

const STATS = [
  { value: '6', label: 'Core compliance & reporting services' },
  { value: 'Monthly & Quarterly', label: 'Ofgem, GGCS and ISCC submissions' },
  { value: 'UK & EU', label: 'Regulatory coverage across both markets' },
  { value: 'Independent', label: 'Impartial, audit-ready guidance' },
];

const WHY_POINTS = [
  'Designed around your certification scheme and market, not a one-size-fits-all template',
  'Independent, practical guidance from experienced consultants',
  'Regulatory reporting built around your obligations, from pre-accreditation to audit',
];

const CHAIN_STEPS = [
  {
    icon: IconShieldCheck,
    step: 'Step 01',
    title: 'Ofgem',
    text: 'The RHI claim for the quarter is submitted.',
  },
  {
    icon: IconCertificate,
    step: 'Step 02',
    title: 'GGCS',
    text: 'A periodic data submission is filed for the Green Gas Certificate Scheme.',
  },
  {
    icon: IconGlobeLeaf,
    step: 'Step 03',
    title: 'ISCC',
    text: 'Mass balance is updated and Proof of Sustainability is issued, via Nabisy or Excel.',
  },
];

function CompliancePage() {
  return (
    <main className="compliance-page">
      {/* ---------- Hero ---------- */}
      <section className="cp-hero">
        <div className="cp-hero-backdrop" aria-hidden="true">
          <span className="cp-glow cp-glow-teal" />
          <span className="cp-glow cp-glow-green" />
        </div>

        <div className="container cp-hero-inner">
          <div className="cp-hero-content">
            <Reveal direction="up">
              <span className="section-label">Compliance & Reporting</span>
            </Reveal>
            <Reveal direction="up" delay={90}>
              <h1 className="cp-hero-title">
                Compliance made straightforward, <span className="cp-accent">from accreditation to audit</span>
              </h1>
            </Reveal>
            <Reveal direction="up" delay={170}>
              <p className="cp-hero-lead">
                We guide producers, traders and users of renewable fuels through Ofgem, EU and UK
                sustainability schemes, turning complex, fast-moving regulation into clear,
                audit-ready compliance you can rely on.
              </p>
            </Reveal>
            <Reveal direction="up" delay={250}>
              <div className="cp-hero-actions">
                <Link to="/#contact" className="btn btn-primary">
                  Talk to our team <IconArrowRight width={17} height={17} />
                </Link>
                <a href="#compliance" className="btn btn-secondary">
                  See our approach
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal direction="right" delay={140}>
            <figure className="cp-hero-visual">
              <div className="cp-hero-frame">
                <img src={heroImage} alt="Consultants reviewing regulatory compliance documentation together" />
                <div className="cp-hero-overlay" />
                <div className="cp-hero-badge">
                  <span className="cp-hero-badge-icon">
                    <IconCertificate width={20} height={20} />
                  </span>
                  <span className="cp-hero-badge-text">
                    <strong>Audit-Ready Support</strong>
                    Independent compliance guidance
                  </span>
                </div>
              </div>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ---------- Stats band ---------- */}
      <section className="cp-stats">
        <div className="container cp-stats-grid">
          {STATS.map((stat, index) => (
            <Reveal direction="up" delay={index * 80} key={stat.label}>
              <div className="cp-stat">
                <span className="cp-stat-value">{stat.value}</span>
                <span className="cp-stat-label">{stat.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- Why work with us ---------- */}
      <section className="section section-tint">
        <div className="container">
          <Reveal direction="up">
            <div className="cp-why">
              <span className="cp-glow cp-glow-green" aria-hidden="true" style={{ opacity: 0.5 }} />
              <div>
                <span className="section-label" style={{ color: 'var(--color-accent-green)' }}>
                  Why Work With Us
                </span>
                <h2 className="cp-why-title">Built around your compliance obligations, not ours</h2>
                <Link to="/#contact" className="btn btn-primary">
                  Get started <IconArrowRight width={17} height={17} />
                </Link>
              </div>

              <ul className="cp-why-list">
                {WHY_POINTS.map((point) => (
                  <li key={point}>
                    <span className="cp-why-check">
                      <IconCheckCircle width={16} height={16} />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Detailed compliance pillars ---------- */}
      <Compliance />

      {/* ---------- Quarterly compliance chain (shared Timeline design) ---------- */}
      <Timeline
        eyebrow="Process"
        title="The Quarterly Compliance Chain"
        subtitle="A closer look at the recurring cycle behind our clients' ongoing regulatory submissions, quarter after quarter."
        steps={CHAIN_STEPS}
      />

      {/* ---------- Final CTA ---------- */}
      <section className="cp-cta">
        <div className="container">
          <div className="cp-cta-card">
            <span className="cp-cta-dots" aria-hidden="true" />
            <span className="cp-cta-blob" aria-hidden="true" />

            <div className="cp-cta-inner">
              <Reveal direction="up">
                <span className="section-label cp-cta-label">Get in Touch</span>
                <h2 className="cp-cta-title">Ready to simplify your compliance?</h2>
                <p className="cp-cta-text">
                  Whether you need Ofgem accreditation, EU RED certification or ongoing carbon
                  reporting, our team is ready to help you stay compliant and protect your market
                  access.
                </p>
                <div className="cp-cta-actions">
                  <a href="tel:01984624989" className="btn btn-primary">
                    <IconPhone width={17} height={17} /> Call 01984 624989
                  </a>
                  <a href="mailto:eviegarner@sustainabilityadvisors.co.uk" className="btn btn-outline-light">
                    <IconMail width={17} height={17} /> Email Us
                  </a>
                </div>
                <p className="cta-address">
                  <IconLocation width={16} height={16} /> Higher Ford, Wiveliscombe, Taunton, Somerset, TA4 2RL
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default CompliancePage;
